/** Formatting utilities */

export function relativeTime(iso: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  // Missing, unparseable, or epoch-fallback dates (new Date(0) → 1970) would
  // render as huge day counts (e.g. "20729d ago"). Show a dash instead.
  if (Number.isNaN(t) || t < 946684800000) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Quarter-hour time display. Billable/working time is shown in 15-minute
 * increments (matching ConnectWise billing granularity): the value is rounded
 * to the nearest 0.25h at the DISPLAY layer only — internal storage stays
 * untouched. ConnectWise owns billing minimums; we only standardize display.
 */
export function formatHours(h: number): string {
  if (!Number.isFinite(h)) return '—';
  const quarters = Math.round(h * 4) / 4; // nearest 0.25h
  return `${quarters.toFixed(2)} ${quarters === 1 ? 'hour' : 'hours'}`;
}

/** Round an hours value to the nearest quarter hour (0.25h step). */
export function toQuarterHours(h: number): number {
  return Math.round(h * 4) / 4;
}

export function isQuarterHours(h: number): boolean {
  return Number.isFinite(h) && Math.abs(h * 4 - Math.round(h * 4)) < Number.EPSILON * 16;
}

export function parseQuarterHours(value: string | number): number | null {
  const hours = typeof value === 'number' ? value : Number(value);
  return isQuarterHours(hours) ? hours : null;
}

export function minutesToHours(minutes: number): number {
  return toQuarterHours(minutes / 60);
}

export function hoursToMinutes(hours: number): number {
  if (!isQuarterHours(hours)) throw new Error('Hours must use 0.25-hour increments');
  return Math.round(hours * 60);
}

export function formatPercent(v: number, decimals = 1): string {
  return `${(v * 100).toFixed(decimals)}%`;
}

export function formatCurrency(v: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

export function formatDateShort(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(iso));
}

export function scoreColor(score: number): string {
  if (score >= 0.85) return 'text-green-600 dark:text-green-400';
  if (score >= 0.7) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

export function scoreBarColor(score: number): string {
  if (score >= 0.85) return 'bg-green-500';
  if (score >= 0.7) return 'bg-amber-500';
  return 'bg-red-500';
}
